'use client';

import { IcCloseCircle, IconButton, TextField, Typography } from '@musinsa/mds';
import React from 'react';
import { IdNumberInfoBottomSheet } from './IdNumberInfoBottomSheet';
import { SETTLEMENT_FORM_SCHEMA } from './settlement-form.schema';

interface IdNumberFieldProps {
  firstValue: string;
  secondValue: string;
  onFirstChange: (value: string) => void;
  onSecondChange: (value: string) => void;
  error?: boolean;
  errorText?: string;
}

export const IdNumberField = React.memo(
  ({
    firstValue,
    secondValue,
    onFirstChange,
    onSecondChange,
    error,
    errorText,
  }: IdNumberFieldProps) => {
    const handleFirstChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9]/g, '');
      if (value.length <= SETTLEMENT_FORM_SCHEMA.validation.idNumber.firstMaxLength) {
        onFirstChange(value);
      }
    };

    const handleSecondChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9]/g, '');
      if (value.length <= SETTLEMENT_FORM_SCHEMA.validation.idNumber.secondMaxLength) {
        onSecondChange(value);
      }
    };

    const handleFirstClear = () => {
      onFirstChange('');
    };

    const handleSecondClear = () => {
      onSecondChange('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.currentTarget.blur();
      }
    };

    return (
      <div className="flex flex-col gap-2 py-2">
        <div className="flex flex-1 justify-between items-center">
          <Typography variant="body14pxMed">주민번호</Typography>
          <IdNumberInfoBottomSheet>
            <Typography
              textDecoration="underline"
              variant="body13pxReg"
              colorScheme="gray-600"
              className="cursor-pointer"
            >
              주민등록번호 처리 안내
            </Typography>
          </IdNumberInfoBottomSheet>
        </div>

        <div className="flex items-center gap-4 w-full">
          <div className="inline-flex relative w-full grow">
            <TextField
              variant="outlined"
              value={firstValue || ''}
              onChange={handleFirstChange}
              onKeyDown={handleKeyDown}
              placeholder="000000"
              maxLength={SETTLEMENT_FORM_SCHEMA.validation.idNumber.firstMaxLength}
              error={error}
              inputMode="numeric"
              enterKeyHint="done"
              className="w-full"
            />
            {firstValue && firstValue.length > 0 && (
              <IconButton
                className="absolute right-2 top-2"
                size="size20x20"
                onClick={handleFirstClear}
                type="button"
              >
                <IcCloseCircle fillColor="gray-400" />
              </IconButton>
            )}
          </div>

          <div className="w-[8px] h-[1px] bg-gray-400 shrink-0" />

          <div className="inline-flex relative w-full grow">
            <TextField
              type="password"
              value={secondValue || ''}
              onChange={handleSecondChange}
              onKeyDown={handleKeyDown}
              placeholder="*******"
              maxLength={SETTLEMENT_FORM_SCHEMA.validation.idNumber.secondMaxLength}
              error={error}
              inputMode="numeric"
              enterKeyHint="done"
              className="w-full"
            />
            {secondValue && secondValue.length > 0 && (
              <IconButton
                className="absolute right-2 top-2"
                size="size20x20"
                onClick={handleSecondClear}
                type="button"
              >
                <IcCloseCircle fillColor="gray-400" />
              </IconButton>
            )}
          </div>
        </div>

        {error && errorText && (
          <Typography variant="textEtc11pxReg" colorScheme="red" className="text-red">
            {errorText}
          </Typography>
        )}
      </div>
    );
  },
);

IdNumberField.displayName = 'IdNumberField';
